export type DismissReason = 'escape' | 'backdrop' | 'close-button';

interface OverlayEntry {
  id: object;
  parentId?: object | null;
  element: HTMLElement;
  root: HTMLElement;
  modal?: boolean;
  restoreFocus?: boolean;
  initialFocus?: HTMLElement | null;
  restoreTarget?: HTMLElement | null;
  onDismiss: (reason: DismissReason) => void;
}

interface RegisteredOverlay extends OverlayEntry {
  previousFocus: HTMLElement | null;
}

const stack: RegisteredOverlay[] = [];
const hiddenElements = new Map<HTMLElement, { hidden: string | null; inert: boolean }>();
const BASE_LAYER = 100;
const LAYER_STEP = 10;
const ESCAPE_KEY_CODE = 27;
const FOCUSABLE =
  'a[href],button,input,select,textarea,[tabindex],iframe,[contenteditable="true"]';
let previousOverflow = '';
let scrollLocked = false;
let redirectingFocus = false;
let observer: MutationObserver | null = null;

const isOwnedBy = (entry: RegisteredOverlay, owner: RegisteredOverlay): boolean => {
  if (entry === owner || owner.element.contains(entry.element)) return true;
  let { parentId } = entry;
  const visited = new Set<object>();
  while (parentId && !visited.has(parentId)) {
    if (parentId === owner.id) return true;
    visited.add(parentId);
    const ancestorId = parentId;
    parentId = stack.find(layer => layer.id === ancestorId)?.parentId;
  }
  return false;
};

const activeModal = (): RegisteredOverlay | undefined =>
  [...stack].reverse().find(layer => layer.modal);
const ownedLayers = (modal: RegisteredOverlay): RegisteredOverlay[] =>
  stack.filter(layer => isOwnedBy(layer, modal));
const containsFocus = (modal: RegisteredOverlay, target: Node): boolean =>
  ownedLayers(modal).some(layer => layer.element.contains(target));
const focusableElements = (element: HTMLElement): HTMLElement[] =>
  Array.from(element.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    control =>
      control.tabIndex >= 0 &&
      !control.matches(':disabled') &&
      !control.closest('[hidden],[inert],[aria-hidden="true"]') &&
      getComputedStyle(control).display !== 'none' &&
      getComputedStyle(control).visibility !== 'hidden',
  );
const focusLayer = (entry: RegisteredOverlay): void => {
  (entry.initialFocus ?? focusableElements(entry.element)[0] ?? entry.element).focus();
};

const syncLayers = (): void => {
  stack.forEach((entry, index) => {
    entry.root.style.zIndex = String(BASE_LAYER + index * LAYER_STEP);
  });
  const modal = activeModal();
  if (modal && !scrollLocked) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    scrollLocked = true;
  } else if (!modal && scrollLocked) {
    document.body.style.overflow = previousOverflow;
    scrollLocked = false;
  }
  const roots = modal ? ownedLayers(modal).map(entry => entry.root) : [];
  const shouldHide = new Set(
    modal
      ? Array.from(document.body.children).filter(
          (element): element is HTMLElement =>
            element instanceof HTMLElement &&
            !element.matches('script,style,[data-overlay-announcement]') &&
            !roots.some(root => element === root || element.contains(root)),
        )
      : [],
  );
  hiddenElements.forEach((original, element) => {
    if (!shouldHide.has(element)) {
      if (original.hidden === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', original.hidden);
      if (!original.inert) element.removeAttribute('inert');
      hiddenElements.delete(element);
    }
  });
  shouldHide.forEach(element => {
    if (!hiddenElements.has(element)) {
      hiddenElements.set(element, {
        hidden: element.getAttribute('aria-hidden'),
        inert: element.hasAttribute('inert'),
      });
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('inert', '');
    }
  });
};

const onFocusIn = (event: FocusEvent): void => {
  const modal = activeModal();
  if (!modal || redirectingFocus || containsFocus(modal, event.target as Node)) return;
  redirectingFocus = true;
  focusLayer(modal);
  redirectingFocus = false;
};

const onKeyDown = (event: KeyboardEvent): void => {
  if (event.defaultPrevented || !stack.length) return;
  if (event.key === 'Escape' || event.keyCode === ESCAPE_KEY_CODE) {
    event.preventDefault();
    stack[stack.length - 1].onDismiss('escape');
    return;
  }
  const modal = activeModal();
  if (event.key !== 'Tab' || !modal) return;
  const controls = Array.from(
    new Set(ownedLayers(modal).flatMap(layer => focusableElements(layer.element))),
  );
  const current = controls.indexOf(document.activeElement as HTMLElement);
  if (!controls.length) {
    event.preventDefault();
    modal.element.focus();
  } else if (
    current < 0 ||
    (event.shiftKey ? current === 0 : current === controls.length - 1)
  ) {
    event.preventDefault();
    controls[event.shiftKey ? controls.length - 1 : 0].focus();
  }
};

export const registerOverlay = (options: OverlayEntry): (() => void) => {
  const entry: RegisteredOverlay = {
    ...options,
    previousFocus:
      options.restoreTarget ?? (document.activeElement as HTMLElement | null),
  };
  const childIndex = stack.findIndex(
    layer => layer.parentId === entry.id || entry.element.contains(layer.element),
  );
  if (childIndex < 0) stack.push(entry);
  else stack.splice(childIndex, 0, entry);
  if (stack.length === 1) {
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    observer = new MutationObserver(syncLayers);
    observer.observe(document.body, { childList: true });
  }
  syncLayers();
  if (
    entry.modal &&
    activeModal() === entry &&
    !containsFocus(entry, document.activeElement!)
  )
    focusLayer(entry);
  else if (stack[stack.length - 1] === entry && entry.initialFocus) focusLayer(entry);
  return () => {
    const index = stack.indexOf(entry);
    if (index < 0) return;
    // Closing a dialog can also unmount its portaled descendants. Restore the
    // dialog's trigger even when one of those descendants occupied the top layer.
    const top = stack[stack.length - 1];
    const wasTop = top === entry || isOwnedBy(top, entry);
    stack.splice(index, 1);
    syncLayers();
    if (!stack.length) {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      observer?.disconnect();
      observer = null;
    }
    if (wasTop && entry.restoreFocus !== false) {
      const modal = activeModal();
      if (
        entry.previousFocus?.isConnected &&
        (!modal || containsFocus(modal, entry.previousFocus))
      )
        entry.previousFocus.focus();
      else if (modal) focusLayer(modal);
    }
  };
};
